import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { ArrowLeft, AlertCircle } from "lucide-react";

interface ErrorScreenProps {
  onRetry: () => void;
  onBack?: () => void;
  title?: string;
  message?: string;
}

export function ErrorScreen({
  onRetry,
  onBack,
  title,
  message,
}: ErrorScreenProps) {
  useLingui();
  const resolvedTitle = title ?? t`Something went wrong`;
  const resolvedMessage = message ?? t`We couldn't complete your request. Please check your connection and try again.`;
  return (
    <div className="mt-[60px] flex flex-col gap-10 px-4 py-6 md:gap-20 md:px-8 md:py-10">
      {onBack && (
        <div className="mx-auto flex w-full max-w-screen-md">
          <button
            onClick={onBack}
            className="py-2.5 hover:bg-muted flex w-fit items-center gap-2 rounded-full bg-[#1E1E1F] px-10 py-6 text-[16px] text-[#ffff6] ring-1 ring-[#fff56] transition-colors hover:ring-[#FFF] md:text-[20px]"
            aria-label={t`Go back`}
          >
            <ArrowLeft />
            <Trans>Back</Trans>
          </button>
        </div>
      )}

      <div className="mx-auto mt-24 flex w-full max-w-screen-sm flex-1 flex-col items-center justify-center md:mt-48">
        <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-[#00EC97]/10 ring-1 ring-[#00EC97]/30 md:mb-8 md:h-20 md:w-20">
          <AlertCircle className="h-8 w-8 text-[#00EC97] md:h-10 md:w-10" aria-hidden />
        </div>

        <h1 className="text-foreground text-balance text-center text-[28px] font-bold md:text-[40px]">{resolvedTitle}</h1>
        <p className="text-muted-foreground leading-relaxed mt-4 max-w-screen-sm text-pretty text-center text-[15px] md:text-[18px]">
          {resolvedMessage}
        </p>
        <p className="text-muted-foreground leading-relaxed mt-4 max-w-screen-sm text-pretty text-center text-[14px] md:text-[16px]">
          <Trans>If the bridge still fails, try the</Trans>{" "}
          <a
            href="https://bridge.assetchain.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="focus-visible:rounded-sm font-medium text-[#00EC97] underline underline-offset-2 transition-colors hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#00EC97]"
          >
            <Trans>official Asset Chain bridge</Trans>
          </a>
          .
        </p>

        <button
          onClick={onRetry}
          className="mt-12 w-full max-w-[404px] rounded-full bg-[#00EC97] py-4 font-semibold text-black transition-all hover:brightness-110 active:scale-[0.98] md:mt-20 md:py-6 md:text-[20px]"
        >
          <Trans>Try again</Trans>
        </button>
      </div>
    </div>
  );
}
